import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { format } from 'date-fns';
import { CalendarIcon, Upload, X, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '../ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../ui/select';
import { Calendar } from '../ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { compressImageToBase64 } from '../../lib/imageCompress';
import { TaxonomyFieldsEditor } from './TaxonomyFieldsEditor';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const EMPTY = {
  category_group: '', subcategory: '', women_activity: '', details: {},
  amount: '', date: new Date(), description: '', program: '',
  payment_method: '', funding_source: '', vendor: '', payee: '',
  reference_number: '', related_project: '', receipt_image: '',
};

export const ExpenseFormDialog = ({ open, onOpenChange, editing, projects, onSaved }) => {
  const [taxonomy, setTaxonomy] = useState(null);
  const [reqRequired, setReqRequired] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    axios.get(`${API_URL}/api/finance/taxonomy`).then((r) => setTaxonomy(r.data)).catch(() => {});
    axios.get(`${API_URL}/api/finance/workflow-config`)
      .then((r) => setReqRequired((r.data.config || []).filter((c) => c.requires_requisition).map((c) => c.subcategory)))
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (!open) return;
    if (editing) {
      setForm({
        category_group: editing.category_group || '', subcategory: editing.subcategory || '',
        women_activity: editing.women_activity || '', details: editing.details || {},
        amount: String(editing.amount ?? ''), date: editing.date ? new Date(editing.date) : new Date(),
        description: editing.description || '', program: editing.program || '',
        payment_method: editing.payment_method || '', funding_source: editing.funding_source || '',
        vendor: editing.vendor || '', payee: editing.payee || '',
        reference_number: editing.reference_number || '', related_project: editing.related_project || '',
        receipt_image: editing.receipt_image || '',
      });
    } else {
      setForm(EMPTY);
    }
  }, [open, editing]);

  const needsRequisition = !editing && form.subcategory && reqRequired.includes(form.subcategory);

  const handleReceiptUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const compressed = await compressImageToBase64(file, { maxDimension: 1280, quality: 0.75 });
      setForm((p) => ({ ...p, receipt_image: compressed }));
      toast.success('Document attached');
    } catch {
      toast.error('Failed to process image');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.category_group || !form.subcategory) {
      toast.error('Select a category and subcategory');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        category_group: form.category_group,
        subcategory: form.subcategory,
        women_activity: form.women_activity || null,
        details: form.details || {},
        amount: parseFloat(form.amount),
        currency: 'UGX',
        date: format(form.date, 'yyyy-MM-dd'),
        description: form.description || null,
        program: form.program || null,
        payment_method: form.payment_method || null,
        funding_source: form.funding_source || null,
        vendor: form.vendor || null,
        payee: form.payee || null,
        reference_number: form.reference_number || null,
        related_project: form.related_project || null,
        receipt_image: form.receipt_image || null,
      };
      if (editing) {
        if (editing.requisition_id) payload.requisition_id = editing.requisition_id;
        if (editing.status) payload.status = editing.status;
        await axios.put(`${API_URL}/api/expenses/${editing.id}`, payload);
        toast.success('Expense updated');
      } else {
        await axios.post(`${API_URL}/api/expenses`, payload);
        toast.success('Expense recorded');
      }
      onOpenChange(false);
      onSaved?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save expense');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-heading">{editing ? 'Edit Expense' : 'Record Expense'}</DialogTitle>
          <DialogDescription>Category → Subcategory → Relevant details → Financial information</DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <TaxonomyFieldsEditor
            taxonomy={taxonomy}
            value={{ category_group: form.category_group, subcategory: form.subcategory, women_activity: form.women_activity, details: form.details }}
            onChange={(v) => setForm((p) => ({ ...p, ...v }))}
            idPrefix="expense"
          />

          {needsRequisition && (
            <div className="flex items-start gap-2 border border-amber-300 bg-amber-50 dark:bg-amber-900/15 text-amber-800 dark:text-amber-200 rounded-lg p-3 text-sm" data-testid="expense-requisition-warning">
              <AlertTriangle className="h-4 w-4 mt-0.5 flex-shrink-0" />
              <span>This expense type requires a <strong>requisition and approval</strong> before payment. Create it under <strong>Finance → Requisitions &amp; Approvals</strong>.</span>
            </div>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div className="space-y-1.5">
              <Label>Amount (UGX) *</Label>
              <Input type="number" required value={form.amount}
                onChange={(e) => setForm({ ...form, amount: e.target.value })} data-testid="expense-amount" />
            </div>
            <div className="space-y-1.5">
              <Label>Date *</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" className="w-full justify-start text-left font-normal" data-testid="expense-date-picker">
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {form.date ? format(form.date, 'PP') : 'Pick date'}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0">
                  <Calendar mode="single" selected={form.date} onSelect={(d) => setForm({ ...form, date: d || new Date() })} initialFocus />
                </PopoverContent>
              </Popover>
            </div>
            <div className="space-y-1.5">
              <Label>Payment Method</Label>
              <Select value={form.payment_method} onValueChange={(v) => setForm({ ...form, payment_method: v })}>
                <SelectTrigger data-testid="expense-payment-method"><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="cash">Cash</SelectItem>
                  <SelectItem value="mobile_money">Mobile Money</SelectItem>
                  <SelectItem value="bank_transfer">Bank Transfer</SelectItem>
                  <SelectItem value="cheque">Cheque</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div className="space-y-1.5">
              <Label>Program</Label>
              <Select value={form.program} onValueChange={(v) => setForm({ ...form, program: v })}>
                <SelectTrigger data-testid="expense-program-select"><SelectValue placeholder="Auto by category" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="school">School Program</SelectItem>
                  <SelectItem value="women">Women Empowerment</SelectItem>
                  <SelectItem value="general">General / Org</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Funding Source</Label>
              <Input value={form.funding_source} placeholder="e.g. NGO Grant Q2"
                onChange={(e) => setForm({ ...form, funding_source: e.target.value })} data-testid="expense-funding-source" />
            </div>
            <div className="space-y-1.5">
              <Label>Related Project</Label>
              <Select value={form.related_project}
                onValueChange={(v) => setForm({ ...form, related_project: v === '__none__' ? '' : v })}>
                <SelectTrigger data-testid="expense-related-project"><SelectValue placeholder="Optional" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">None</SelectItem>
                  {(projects || []).map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div className="space-y-1.5">
              <Label>Vendor / Supplier</Label>
              <Input value={form.vendor} onChange={(e) => setForm({ ...form, vendor: e.target.value })} data-testid="expense-vendor" />
            </div>
            <div className="space-y-1.5">
              <Label>Payee</Label>
              <Input value={form.payee} placeholder="Who was paid"
                onChange={(e) => setForm({ ...form, payee: e.target.value })} data-testid="expense-payee" />
            </div>
            <div className="space-y-1.5">
              <Label>Reference No.</Label>
              <Input value={form.reference_number} placeholder="e.g. VCH-0031"
                onChange={(e) => setForm({ ...form, reference_number: e.target.value })} data-testid="expense-reference" />
            </div>
          </div>

          <div className="space-y-1.5">
            <Label>Description</Label>
            <Input value={form.description} placeholder="Brief description"
              onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="expense-description" />
          </div>

          <div className="space-y-1.5">
            <Label>Supporting Document / Receipt</Label>
            <label className="flex items-center justify-center gap-2 px-3 py-2 border rounded-md cursor-pointer hover:bg-muted text-sm" data-testid="expense-receipt-upload-label">
              <Upload className="h-4 w-4" /> <span>Upload receipt / invoice</span>
              <input type="file" accept="image/*" className="hidden" onChange={handleReceiptUpload} data-testid="expense-receipt-upload-input" />
            </label>
            {form.receipt_image && (
              <div className="relative inline-block">
                <img src={form.receipt_image} alt="Receipt" className="max-h-28 rounded-md border" />
                <Button type="button" size="icon" variant="destructive" className="absolute -top-2 -right-2 h-6 w-6"
                  onClick={() => setForm((p) => ({ ...p, receipt_image: '' }))} data-testid="remove-receipt-btn">
                  <X className="h-3 w-3" />
                </Button>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button type="submit" disabled={saving || needsRequisition} data-testid="expense-submit-btn">
              {editing ? 'Update Expense' : 'Save Expense'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
